import React, { useState, useMemo, useEffect } from "react";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
	Autocomplete,
	TextField,
	CircularProgress,
	useMediaQuery,
	styled,
	Typography,
	Box,
	Skeleton,
	Button,
	Tooltip,
} from "@mui/material";
import { useSite } from "../../context/siteContext";
import { useAuth } from "../../context/authContext";
import { useOptions } from "../../context/optionContext";
import { useTranslation } from "react-i18next";
import { PLACEHOLDER } from "../../constants";
import { updateRecentSites as updateRecentSitesService } from "../../services/userService";
import { useLocation } from "react-router-dom";
import "./siteselection.css";
import { DEFAULT_LAYOUT } from "../../config";

const GroupHeader = styled("div")(({ theme }) => ({
	position: "sticky",
	top: 0,
	padding: "4px 8px",
	color: "#181818",
	backgroundColor: "#eee",
	fontWeight: 600,
	fontSize: "12.5px",
}));

const GroupItems = styled("ul")({
	padding: 0,
	margin: 0,
	listStyle: "none",
});

const isEnabled = (value, defaultValue = true) => {
	if (value === undefined || value === null) return defaultValue;
	if (typeof value === "boolean") return value;
	return value !== "false" && value !== "0";
};

const SiteSelection = () => {
	const { selectedSite, updateSite, siteOptions, loading, siteData, isAdminAllSites } = useSite();
	const { user, updateUserStatus } = useAuth();
	const { optionKeys } = useOptions();
	const isMobile = useMediaQuery("(max-width: 768px)");
	const { t } = useTranslation();
	const location = useLocation();
	const isAdminRoute = useMemo(
		() => (location.pathname || "").toLowerCase().startsWith("/admin"),
		[location.pathname]
	);

	const [inputValue, setInputValue] = useState("");
	const [debouncedInput, setDebouncedInput] = useState("");
	const [loadingOptions, setLoadingOptions] = useState(false);
	const [isManuallyOpened, setIsManuallyOpened] = useState(false);

	// 🔁 Recently selected sites (persisted in localStorage with FIFO max 5)
	const RECENT_SITES_KEY = "iecom_recent_sites";
	const [recentSiteValues, setRecentSiteValues] = useState([]); // array of option.value strings

	const persistRecentSites = async (nextValues) => {
		const limited = nextValues.slice(0, 5);
		setRecentSiteValues(limited);
		try {
			localStorage.setItem(RECENT_SITES_KEY, JSON.stringify(limited));
		} catch (_) {
			// ignore storage errors
		}
		if (typeof updateUserStatus === "function") {
			updateUserStatus({ recent_sites: limited });
		}
		try {
			await updateRecentSitesService(limited);
		} catch (err) {
			console.error("Failed to persist recent sites", err);
		}
	};

	useEffect(() => {
		try {
			const raw = typeof window !== "undefined" ? localStorage.getItem(RECENT_SITES_KEY) : null;
			let source = [];
			if (raw) {
				const parsed = JSON.parse(raw);
				if (Array.isArray(parsed)) source = parsed;
			}
			if (source.length === 0 && Array.isArray(user?.recent_sites)) {
				source = user.recent_sites;
			}
			const validSet = new Set(siteOptions.map((o) => o.value));
			const pruned = source.filter((v) => validSet.has(v));
			if (pruned.length > 0) {
				setRecentSiteValues(pruned.slice(0, 5));
			}
		} catch (err) {
			console.error("Failed to load recent sites", err);
		}
	}, [siteOptions, user?.recent_sites]);

	// ⏳ Debounce inputValue updates, but skip when opening
	useEffect(() => {
		if (isManuallyOpened) return; // Skip debounce right after open

		if (inputValue.trim() === "") {
			setDebouncedInput("");
			setLoadingOptions(false);
			return;
		}

		setLoadingOptions(true);
		const handler = setTimeout(() => {
			setDebouncedInput(inputValue);
			setLoadingOptions(false);
		}, 500);

		return () => clearTimeout(handler);
	}, [inputValue, isManuallyOpened]);

	const adminAllSitesText =
		t('UI_TEXT.ADMIN_ALL_SITES_ACCESS', { defaultValue: "Admin access: all sites available." });
	const adminAllSitesHint =
		t('UI_TEXT.ADMIN_ALL_SITES_HINT', { defaultValue: "Showing recent sites by default. Search to see more." });

	// 🔍 Filter and limit options (with recent selections logic)
	const filteredOptions = useMemo(() => {
		const searchTerm = debouncedInput.trim().toLowerCase();

		let matches;

		// Show top 5 default options when no search term — preference to recent selections
		if (!searchTerm) {
			const recentSet = new Set(recentSiteValues);
			const recentOptions = recentSiteValues
				.map((v) => siteOptions.find((o) => o.value === v))
				.filter(Boolean);
			const remaining = siteOptions.filter((o) => !recentSet.has(o.value));
			const fillCount = Math.max(0, 5 - recentOptions.length);
			matches = [...recentOptions, ...remaining.slice(0, fillCount)];
		} else {
			const fieldIncludesTerm = (value) => {
				if (value === undefined || value === null) return false;
				return String(value).toLowerCase().includes(searchTerm);
			};
			matches = siteOptions.filter((opt) => {
				return (
					fieldIncludesTerm(opt.searchTokens) ||
					fieldIncludesTerm(opt.text) ||
					fieldIncludesTerm(opt.value) ||
					fieldIncludesTerm(opt.customer_site_name) ||
					fieldIncludesTerm(opt.customer_site_number) ||
					fieldIncludesTerm(opt.parent_customer_name) ||
					fieldIncludesTerm(opt.parent_customer_number) ||
					fieldIncludesTerm(opt.customer_name) ||
					fieldIncludesTerm(opt.customer_number)
				);
			});
		}

		// Always include selected option; keep to 5 for default view
		const selected = siteOptions.find(
			(opt) => opt.value === selectedSite || opt.text === selectedSite
		);
		if (selected && !matches.some((m) => m.value === selected.value)) {
			if (!searchTerm) {
				matches = [selected, ...matches].slice(0, 5);
			} else {
				matches = [selected, ...matches];
			}
		}

		if (isAdminAllSites && !searchTerm) {
			const hintOption = {
				value: "__admin_all_sites_hint__",
				text: `${adminAllSitesText} ${adminAllSitesHint}`,
				isSearchMore: true,
				disabled: true,
			};
			return [hintOption, ...matches];
		}

		return matches;
	}, [adminAllSitesHint, adminAllSitesText, debouncedInput, isAdminAllSites, recentSiteValues, selectedSite, siteOptions]);

	const globalDisplayOption = useMemo(
		() => ({
			value: "__global_site__",
			text: t("LABELS.GLOBAL") || "Global",
			disabled: true,
		}),
		[t]
	);

	const autocompleteValue = useMemo(() => {
		if (isAdminRoute) return globalDisplayOption;
		return (
			siteOptions.find(
				(opt) => opt.value === selectedSite || opt.text === selectedSite
			) || null
		);
	}, [globalDisplayOption, isAdminRoute, selectedSite, siteOptions]);

	const autocompleteOptions = isAdminRoute ? [globalDisplayOption] : filteredOptions;
	const helperTextKey = (isAdminRoute || DEFAULT_LAYOUT.toLowerCase() === "layout2") ? null : 'PLACEHOLDER.CHANGE_CUSTOMER';
	const inputPlaceholder = isAdminRoute ? t('PLACEHOLDER.GLOBAL_SITE_OPTION') : t('PLACEHOLDER.SELECT_SITE');
	const adminTooltip = t('UI_TEXT.ADMIN_SITE_LOCK') || t('EULA_ADMIN.SITE_SELECTION_IS_LOCKED_WHILE_ON_ADMIN_PAGE');
	const groupingEnabled = isEnabled(optionKeys?.siteSelectionGroupingEnabled, true);
	const showSiteNumber = isEnabled(optionKeys?.siteSelectionShowSiteNumber, true);

	if (loading) {
		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, width: 250 }}>
				<Skeleton variant="circular" width={30} height={30} />
				<Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
					<Skeleton variant="text" width="80%" height={28} />
					<Skeleton variant="text" width="60%" height={20} />
				</Box>
			</Box>
		);
	}

	const handleChange = (event, newValue) => {
		if (isAdminRoute) return;
		if (newValue && !newValue.disabled) {
			const chosenValue = newValue.value || newValue;
			updateSite(chosenValue, siteData);
			const next = [chosenValue, ...recentSiteValues.filter((v) => v !== chosenValue)].slice(0, 5);
			persistRecentSites(next);
		}
	};

	return (
		<div
			className="site-select"
			style={{
				display: "flex",
				alignItems: "center",
				padding: "10px",
				cursor: "pointer",
				...(DEFAULT_LAYOUT.toLowerCase() === "layout2" && {
					border: "1px solid",
					borderColor: '#ccc',
					borderRadius: '20px',
					padding: "4px 8px",
				}),
			}}
		>

			{DEFAULT_LAYOUT.toLowerCase() !== "layout2" ? (<>
				<Tooltip title={isAdminRoute ? adminTooltip : ''} disableHoverListener={!isAdminRoute}>
					<Button variant="roundedGreyBgSquareContained" sx={{ mr: 1 }} color="#494949">
						<LocationOnIcon />
					</Button>
				</Tooltip>
			</>) : (<>
				<Tooltip title={isAdminRoute ? adminTooltip : ''} disableHoverListener={!isAdminRoute}>
					<Button variant="roundedGreyBgSquareContainedSmall" sx={{ mr: 1 }} color="#494949">
						<LocationOnIcon sx={{ fontSize: '14px' }} />
					</Button>
				</Tooltip>
			</>)}
			<div style={{ display: "flex", flexDirection: "column" }}>
				<Autocomplete
					disableClearable
					disabled={isAdminRoute}
					loading={loadingOptions && !isAdminRoute}
					options={autocompleteOptions}
					filterOptions={(options) => options}
					isOptionEqualToValue={(option, value) => option?.value === value?.value}
					getOptionLabel={(option) => option.text || option.label || ""}
					groupBy={groupingEnabled ? (option) => option.parent_customer_name || "" : undefined}
					value={autocompleteValue}
					onOpen={() => {
						if (isAdminRoute) return;
						setIsManuallyOpened(true);
						setInputValue("");
						setDebouncedInput("");
						// Reset the flag after a short time so debounce works again
						setTimeout(() => setIsManuallyOpened(false), 300);
					}}
					onChange={handleChange}
					inputValue={isAdminRoute ? "" : inputValue}
					onInputChange={(event, newInputValue) => {
						if (isAdminRoute) return;
						setInputValue(newInputValue);
					}}
					noOptionsText={
						loadingOptions ? t('GENERAL.LOADING_LOCATIONS') : t('GENERAL.NO_MATCHES_FOUND')
					}
					renderInput={(params) => (
						<TextField
							{...params}
							placeholder={inputPlaceholder}
							variant="standard"
							onFocus={() => {
								if (isAdminRoute) return;
								setIsManuallyOpened(true);
								setInputValue("");
								setDebouncedInput("");
								setTimeout(() => setIsManuallyOpened(false), 300);
							}}
							InputProps={{
								...params.InputProps,
								disableUnderline: true,
								endAdornment: (
									<>
										{loadingOptions && !isAdminRoute ? (
											<CircularProgress color="primary" size={16} />
										) : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
							sx={{
								"& .MuiInputBase-root": {
									border: "none",
									fontWeight: "bold",
									color: "#4f4f4f",
									padding: 0,
									width: isMobile ? "100%" : DEFAULT_LAYOUT.toLowerCase() === "layout2" ? 225 : 165,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								},
							}}
						/>
					)}
					renderOption={(props, option) => {
						if (option.isSearchMore) {
							return (

								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "flex-start",
										rowGap: 0,
										width: "100%",
										px: 1,
										py: 1,
									}}
								>
									<Typography variant="body1" fontSize={"10px"} color="text.secondary" fontWeight={"bold"} fontStyle={"italic"}>
										{option.text}
									</Typography>

								</Box>

							);
						}

						return (
							<li {...props}>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "flex-start",
										rowGap: 0,
										width: "100%",
										px: 0.5,
										py: 0.5,
									}}
								>
									<Typography variant="body1" fontSize={"13px"} fontWeight={"bold"}>
										{option.text}
									</Typography>
									{showSiteNumber && (
										<Typography variant="body1" fontSize={"12px"} color="text.secondary">
											{option.value}
										</Typography>
									)}
								</Box>
							</li>
						);
					}}
					renderGroup={
						groupingEnabled
							? (params) => (
								<li key={params.key}>
									{params.group !== "" && <GroupHeader>{params.group}</GroupHeader>}
									<GroupItems>{params.children}</GroupItems>
								</li>
							)
							: undefined
					}
					slotProps={{ paper: { sx: { minWidth: 200 } } }}
					sx={{
						width: isMobile ? "100%" : DEFAULT_LAYOUT.toLowerCase() === "layout2" ? 225 : 165,
						height: "15px",
						color: '#4f4f4f',
						"& .MuiOutlinedInput-root": {
							border: "none",
							height: "15px",
							"& fieldset": { border: "none" },
							"&:hover fieldset": { border: "none" },
							"&.Mui-focused fieldset": { border: "none" },
						},
						"& .MuiInput-root": {
							height: "15px",
						},
					}}
				/>
				{helperTextKey && (
					<Typography variant="body2" fontSize={"11px"} mt={0.55} color="text.secondary">
						{t(helperTextKey)}
					</Typography>
				)}
			</div>
		</div>
	);
};

export default SiteSelection;
